// tenantGuard — pure helpers that decide whether an in-app navigation
// should be redirected to the branded 404 surface when the current
// tenant subdomain doesn't resolve. Extracted so the branching can
// be unit-tested without bootstrapping a full Vue router.
//
// Why this exists (#1499/#1503): App.vue.onMounted checks
// useTenant().notFound on initial boot and bounces to error-not-found.
// That guard runs ONCE. After the user lands on the 404 page and
// clicks "Tilbake til forsiden" the SPA performs an in-app navigation
// to `/`. The mount-time check has already fired, the tenant fetch
// has already failed, and BookingLayout proceeds to render with a
// null tenant — empty `<h1>`, "Ingen tjenester er tilgjengelige",
// continued 404s on /api/services + /api/admin/me. The visitor sits
// in an inconsistent state where the URL says home but the surface
// is just as broken as the 404 page was, without any error copy.
//
// The fix is a per-navigation guard so notFound is re-checked on
// every push/replace, not just on mount.

import type { RouteLocationNormalized } from 'vue-router'

// resolveTenantNotFoundRedirect returns the redirect target if the
// destination should be bounced to the branded 404 surface, or null
// when the navigation is allowed to proceed.
//
// Allow-list (returns null, navigation proceeds):
//   - tenantNotFound is false — the normal happy path.
//   - destination is the error-not-found route — we're already there.
//
// Everything else when tenantNotFound is true redirects to
// error-not-found. We don't try to special-case `/auth/*`, `/portal`,
// etc. because there's no recoverable surface on a non-existent
// tenant subdomain — every API call will 404, every layout will
// render with empty state. The branded 404 page is the only honest
// destination.

export function resolveTenantNotFoundRedirect(
  to: Pick<RouteLocationNormalized, 'name'>,
  tenantNotFound: boolean,
): { name: 'error-not-found' } | null {
  if (!tenantNotFound) return null
  if (to.name === 'error-not-found') return null
  return { name: 'error-not-found' }
}
